import {
  type TimelineColorThemesKinds,
  type TimelineConnectorKinds,
  type TimelineMedallionKinds,
} from '@saviynt/design-system';

import {
  type AUDIT_TRAIL_MAP,
  type GLOBAL_ALERT_BANNER_TYPES,
} from '../constants';
import { type NavigateToDetailsProps } from '../hooks';

import { type ListRowAccountState, type SystemDataThemeType } from './types';

// Global types
export enum GlobalType {
  SLICE_NAME = 'aag/global',
  API_SLICE_NAME = 'aag/globalApi',
}

export interface AlertBannerState {
  kind: (typeof GLOBAL_ALERT_BANNER_TYPES)[keyof typeof GLOBAL_ALERT_BANNER_TYPES];
  title: string;
  description: string;
  link?: {
    text: string;
    onClick: (props: unknown) => void | undefined | null;
    suffixIcon: string;
  };
  isOpen: boolean;
  dataTheme: SystemDataThemeType;
}

export interface GlobalState {
  dataTheme: SystemDataThemeType;
  globalDateFormat: string;
  isGlobalLoading: boolean;
  isGlobalError: boolean;
  globalErrorReason: string;
  activeViolationsTab: string;
  mediaQueryValue: number;
  alertBanner: AlertBannerState;
  isReadOnlyAction: boolean;
}

// API reponses
interface GetApiResponse {
  status?: number;
  errorCode?: string;
  displayRecords?: number;
  displayCount?: number;
  displaycount?: number;
}

export interface FunctionData {
  functionKey: string;
  functionName: string;
}

export interface TrackedFunctionData extends FunctionData {
  key: number;
}

export interface SodSidePanelSummaryData {
  latestActivity: {
    violationId: string;
    auditEventType: string;
    tags: [];
    eventTimestamp: string;
    changeLog: [];
  } | null;
  violationData: {
    actualVsPotential: string;
    status: string;
    risk: {
      ruleset: string | null;
      riskType: string | null;
      riskPriority: string | null;
      riskSeverity: string | null;
      riskScore: string | null;
      riskDescription: string | null;
      function1?: FunctionData;
      function2?: FunctionData;
      function3?: FunctionData;
      function4?: FunctionData;
      function5?: FunctionData;
      violationAge: number | null;
    };
    user: {
      firstName: string;
      lastName: string;
      username: string;
      displayName: string;
    };
    userCapability: {
      allowedActions: string[];
      readOnly: boolean;
    };
    violationAge: string;
    accounts: {
      accountName: string;
      accountDisplayName: string;
    }[];
    endpoints: {
      endpointLogo: string | null;
      endpointName: string | null;
    }[];
    riskDescription: string;
    ruleset: string;
    violationOrigin: string;
    businessProcess: string;
    riskOwner: string;
  };
}
export interface GetSummaryDetailsApiResponse extends GetApiResponse {
  data: { violationData: SodSidePanelSummaryData };
}

export interface SodSidePanelUserData {
  firstname: string;
  lastname: string;
  displayname: string;
  username: string;
  systemUserName: string;
  email: string;
  employeeid: string;
  country: string;
  city: string;
  employeetype: string;
  employeeclass: string;
  jobdescription: string;
  manager: string;
  location: string;
  companyname: string;
  costcenter: string;
  region: string;
  entity: string;
  orgunitid: string;
  identitytype: string;
  userstatus: string;
  jobDescription: string;
}

export interface GetUserDetailsApiResponse extends GetApiResponse {
  userdetails?: SodSidePanelUserData;
}

type AuditEventType = keyof typeof AUDIT_TRAIL_MAP;

export interface ChangeLogItem {
  key: string;
  value: string;
  objects?: { value: string }[][];
}

interface Tags {
  key: string;
  value: string;
}

export interface SodSidePanelHistoryData {
  violationId: string;
  auditEventType: AuditEventType;
  tags: Tags[] | [];
  eventTimestamp: string;
  changeLog: ChangeLogItem[];
}

export interface HistoryTimelineMappedData {
  title: string | React.JSX.Element;
  attributes: {
    label: string;
    detail: string | React.JSX.Element;
  }[];
  connectorKind: TimelineConnectorKinds;
  medallionKind: TimelineMedallionKinds;
  icon: string;
  colorTheme: TimelineColorThemesKinds;
}

export interface GetHistoryDetailsApiResponse extends GetApiResponse {
  data: SodSidePanelHistoryData[];
}

export interface SodAcessPathItems {
  id: number | string;
  title: string;
  subtitle: string;
}

export interface AccessPathParams {
  violationId: string;
  assignedEntitlementId: string;
  violatingEntitlementId: string;
}

export interface GetAccessPathApiResponse extends GetApiResponse {
  data: AccessPathParams[];
}

export interface GetActionStatusResponse extends GetApiResponse {
  data: {
    allowedActions: string[];
    readOnly: boolean;
  };
}

export interface ViolationsActionManagerState {
  isOpen: boolean;
  sodKey: string;
  action: string;
  onFail?: () => void;
  onSuccess?: () => void;
  isBulkAction?: boolean;
  // Below are action specific
  title?: string;
  userName?: string;
  displayName?: string;
  allowedActions?: string[];
  isReadOnlyActions?: boolean;
  riskId?: string;
  rulesetName?: string;
  functions?: TrackedFunctionData[];
  accounts?: ListRowAccountState[];
  redirectProps?: NavigateToDetailsProps;
  mitigatingControlCount?: number;
  startDate?: string | null;
  endDate?: string | null;
  mcKeys?: number[];
}
